import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Plus, Download, DollarSign, Clock, CheckCircle, Loader2 } from 'lucide-react';

export default function Invoices({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedLoadId, setSelectedLoadId] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadInvoices();
    loadLoads();
  }, []);

  const loadInvoices = () => {
    api.get('/invoices')
      .then(res => setInvoices(res.data))
      .catch(err => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  };

  const loadLoads = () => {
    api.get('/loads')
      .then(res => setLoads(res.data))
      .catch(err => console.error(err));
  };

  const createInvoice = async () => {
    if (!selectedLoadId) {
      toast.error('Please select a load');
      return;
    }

    setCreating(true);
    try {
      await api.post('/invoices', { load_id: selectedLoadId });
      toast.success('Invoice created!');
      setShowCreateDialog(false);
      setSelectedLoadId('');
      loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: newStatus });
      toast.success(`Invoice marked as ${newStatus}`);
      loadInvoices();
    } catch (err) {
      toast.error('Failed to update invoice');
    }
  };

  const downloadPdf = (invoiceId) => {
    window.open(`${api.defaults.baseURL}/invoices/${invoiceId}/pdf`, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-slate-100 text-slate-700';
      case 'sent': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <FileText className="w-4 h-4" />;
      case 'sent': return <Clock className="w-4 h-4" />;
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  // Stats
  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    totalAmount: invoices.reduce((sum, i) => sum + (i.total_amount || 0), 0),
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total_amount || 0), 0),
    pendingAmount: invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + (i.total_amount || 0), 0)
  };

  // Get loads that don't have invoices yet
  const loadsWithoutInvoices = loads.filter(load => 
    !invoices.some(inv => inv.load_id === load.id)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-600 mt-1">Generate and manage invoices for your loads</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Invoice from Load</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select a Load</Label>
                <Select value={selectedLoadId} onValueChange={setSelectedLoadId}>
                  <SelectTrigger data-testid="load-select">
                    <SelectValue placeholder="Choose a load..." />
                  </SelectTrigger>
                  <SelectContent>
                    {loadsWithoutInvoices.length === 0 ? (
                      <SelectItem value="none" disabled>No loads without invoices</SelectItem>
                    ) : (
                      loadsWithoutInvoices.map(load => (
                        <SelectItem key={load.id} value={load.id}>
                          {load.load_number} - ${load.rate_to_shipper?.toLocaleString()} 
                          ({load.pickup_city} → {load.delivery_city})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Invoice will be generated using the load's shipper rate and details
                </p>
              </div>
              <Button 
                onClick={createInvoice} 
                className="w-full" 
                disabled={creating || !selectedLoadId}
                data-testid="confirm-create-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Invoice'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Invoices</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-blue-600">{stats.sent}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Paid</p>
                <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Outstanding</p>
                <p className="text-2xl font-bold text-orange-600">${stats.pendingAmount.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle>All Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No invoices yet</p>
              <p className="text-sm">Create your first invoice from a completed load</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-slate-600">Invoice #</th>
                    <th className="pb-3 font-medium text-slate-600">Load</th>
                    <th className="pb-3 font-medium text-slate-600">Shipper</th>
                    <th className="pb-3 font-medium text-slate-600">Amount</th>
                    <th className="pb-3 font-medium text-slate-600">Status</th>
                    <th className="pb-3 font-medium text-slate-600">Date</th>
                    <th className="pb-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invoices.map(invoice => (
                    <tr key={invoice.id} className="hover:bg-slate-50" data-testid={`invoice-row-${invoice.id}`}>
                      <td className="py-3 font-medium">{invoice.invoice_number}</td>
                      <td className="py-3 text-sm text-slate-600">{invoice.load_number}</td>
                      <td className="py-3 text-sm">{invoice.shipper_name}</td>
                      <td className="py-3 font-medium text-green-600">
                        ${invoice.total_amount?.toLocaleString()}
                      </td>
                      <td className="py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {getStatusIcon(invoice.status)}
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-slate-500">
                        {new Date(invoice.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => downloadPdf(invoice.id)}
                            data-testid={`download-${invoice.id}`}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            PDF
                          </Button>
                          {invoice.status === 'draft' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateInvoiceStatus(invoice.id, 'sent')}
                            >
                              Mark Sent
                            </Button>
                          )}
                          {invoice.status === 'sent' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600 border-green-300"
                              onClick={() => updateInvoiceStatus(invoice.id, 'paid')}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
